import { useAutoneTranslation } from '@autone/translations';
import {
  Badge,
  BadgeGroup,
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellTitle,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { type APP_NAME } from '../../../consts';

const COLUMN_ID = 'product_groups' as const;

export type ProductGroupsColumnId = typeof COLUMN_ID;

interface ProductGroupsFields {
  product_groups: string[] | null;
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<ProductGroupsFields>();

export const createProductGroupsColumn = <
  T extends ProductGroupsFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.product_groups),
    {
      id: COLUMN_ID,
      size: 150,
      header: function ProductGroupsHeader() {
        const { common } = useAutoneTranslation<typeof APP_NAME>();
        return (
          <DataTableCellContent align={'left'}>
            <DataTableCellTitle className="uppercase font-medium">
              {common.withCustomer('$product-groups')}
            </DataTableCellTitle>
          </DataTableCellContent>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function ProductGroupsCell({ getValue }) {
            const { common } = useAutoneTranslation<typeof APP_NAME>();

            const maxBadges = 2;
            const groups = getValue();
            const productGroupBadges = useMemo(
              () => groups?.map((name) => ({ id: name, label: name })) ?? [],
              [groups],
            );

            const totalProductGroups = productGroupBadges.length;

            if (totalProductGroups === 0) {
              return <Badge variant={'grey'}>{common('na')}</Badge>;
            }

            return (
              <div className="flex gap-1 flex-wrap">
                <BadgeGroup badges={productGroupBadges} maxBadges={maxBadges} />
              </div>
            );
          },
        }),
    },
  );
